import type { Assessment } from './lib/assessment';
import { mockAssessment } from './lib/mockAnalysis';
import { CLIENT_ANALYSIS_TIMEOUT_MS } from './lib/validation';

export type AnalyseInput = {
  resume: string;
  jobAd: string;
};

export async function analyse(input: AnalyseInput): Promise<Assessment> {
  const useMock = import.meta.env.VITE_USE_MOCK === 'true' || new URLSearchParams(window.location.search).has('demo');
  if (useMock) {
    await new Promise((resolve) => window.setTimeout(resolve, 700));
    return mockAssessment;
  }

  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), CLIENT_ANALYSIS_TIMEOUT_MS);

  try {
    const response = await fetch('/api/analyse', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
      signal: controller.signal,
    });
    const payload = await response.json().catch(() => null) as { assessment?: Assessment; error?: string } | null;

    if (!response.ok || !payload?.assessment) {
      throw new Error(payload?.error || 'We could not complete the assessment. Please try again.');
    }

    return payload.assessment;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('The AI assessment did not finish in time. Please try again; if it keeps happening, shorten the resume or job ad slightly.');
    }
    throw error instanceof Error ? error : new Error('Something went wrong. Please try again.');
  } finally {
    window.clearTimeout(timeout);
  }
}
